import { useDraggable } from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@uturn/api';
import { getFetchAllGroupQueryKey, useUpdateGroup } from '@uturn/api/v2';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	cn,
	sonner,
} from '@uturn/ui-kit';
import type React from 'react';
import { useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useGroupContext } from '../../context/group-context';
import type { TGroup } from '../../pages/overview';
import { Draggable } from '../draggable';

type Carrier = {
	id: number;
	name: string;
	email: string;
};

type Props = {
	carrier: Carrier;
	group: TGroup;
	id: string;
	isPending: boolean;
};

const GroupItem: React.FC<Props> = ({ id, group, isPending, carrier }) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { mutateAsync: mutateUpdateGroup } = useUpdateGroup();
	const [_, startTransition] = useTransition();
	const { setGroups, setOptimisticGroups } = useGroupContext();
	const { attributes, listeners, setNodeRef } = useDraggable({
		id,
		data: {
			title: carrier.name,
			subtitle: carrier.email,
			actualId: carrier.id,
			groupId: group.groupId,
		},
	});

	const removeMemberFromGroup = async () => {
		const updatedCarrierIds =
			group.carriers
				?.filter((row) => row.organizationId !== carrier.id)
				.map((row) => row.organizationId) ?? [];

		setOptimisticGroups({
			type: 'REMOVE_CARRIER_FROM_GROUP',
			carrierId: carrier.id,
			groupId: group.groupId,
		});

		startTransition(async () => {
			try {
				await mutateUpdateGroup(
					{
						id: group.groupId,
						data: {
							carrierIds: updatedCarrierIds,
							groupName: group.groupName,
						},
					},
					{
						onSuccess: async () => {
							sonner.success(
								t(
									'remove-carrier.toast.success-message',
									'Successfully removed carrier from "{{name}}"',
									{
										name: group.groupName,
									},
								),
							);
							await queryClient.invalidateQueries({
								queryKey: getFetchAllGroupQueryKey(),
							});
						},
						onError: (error) => {
							if (isAxiosError(error)) {
								sonner.error(
									t(
										'remove-carrier.toast.error-message',
										'Failed to remove carrier from "{{name}}", please try again',
										{
											name: group.groupName,
										},
									),
								);
								return;
							}

							sonner.error(
								t(
									'toast.generic-error-message',
									'An unexpected error occurred. Please contact support',
								),
							);
						},
					},
				);

				setGroups((state) => {
					const groupIdx = state.findIndex(
						(row) => row.groupId === group.groupId,
					);
					if (groupIdx === -1) {
						return state;
					}

					const result = [...state];
					result[groupIdx] = {
						...group,
						carriers: group.carriers?.filter(
							(row) => row.organizationId !== carrier.id,
						),
					};

					return result;
				});
			} catch (error) {
				console.error(error);
			}
		});
	};

	return (
		<Draggable>
			<div
				ref={setNodeRef}
				{...attributes}
				className={cn(
					'flex gap-1 items-center justify-between hover:bg-gray-100 rounded-md p-2 cursor-default z-100 relative',
					isPending && 'opacity-25',
				)}
			>
				<div className="flex gap-1 items-center">
					<Draggable.Handle {...listeners} />
					<div>
						<Draggable.Title>{carrier.name}</Draggable.Title>
						<div className="flex items-center gap-2">
							<Draggable.Info>{carrier.name}</Draggable.Info>
						</div>
					</div>
				</div>
				<AlertDialog>
					<AlertDialogTrigger asChild>
						<Draggable.Trash />
					</AlertDialogTrigger>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t('modal.alert.title', 'Are you sure?')}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t(
									'modal.alert.remove-carrier.description',
									'This will remove "{{ name }}" from group "{{ group }}", this action can not be undone.',
									{
										name: carrier.name,
										group: group.groupName,
									},
								)}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>
								{t('modal.alert.cancel', 'Cancel')}
							</AlertDialogCancel>
							<AlertDialogAction onClick={() => removeMemberFromGroup()}>
								{t('modal.alert.confirm.remove', 'Yes, Remove')}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</Draggable>
	);
};

export default GroupItem;
